/**
 * HelpPage — public, in-app documentation for FormFlo.
 *
 * Structured as a left-rail TOC + scrollable content. Linkable section anchors.
 * Lives under /help so unauthenticated users can read it.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    FilesIcon, GitBranch, ShareNetwork, UsersThree, Stack, Sparkle, Palette,
    Database, MagnifyingGlass, BookOpen, Wrench, ArrowSquareOut, ListNumbers,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

const SECTIONS = [
    {
        id: "getting-started",
        icon: BookOpen,
        title: "Getting started",
        body: () => (
            <>
                <p>FormFlo is a multi-tenant low-code platform: define forms in a UI, attach approval workflows, and route data through your team. This guide walks through every feature an admin or builder needs.</p>
                <ol>
                    <li><strong>Create your workspace</strong> — sign up at <code>/signup</code>. You'll receive a 6-digit OTP to verify the workspace email.</li>
                    <li><strong>Invite teammates</strong> — Users → New user. Each user belongs to one org and can be a member of multiple groups.</li>
                    <li><strong>Build your first form</strong> — Forms → New form, then jump to the Builder.</li>
                </ol>
            </>
        ),
    },
    {
        id: "form-types",
        icon: Stack,
        title: "Form types",
        body: () => (
            <>
                <p>Every form has a <em>layout type</em> that controls how it's rendered to users:</p>
                <ul>
                    <li><strong>Single</strong> — all sections on one page (default). Best for short forms.</li>
                    <li><strong>Stepper</strong> — one section at a time with a Next/Back navigation. Best for guided data entry where order matters.</li>
                    <li><strong>Tabs</strong> — sections appear as tabs at the top. Best for forms with parallel concerns (e.g. "Personal", "Address", "Payment").</li>
                </ul>
                <p>Stepper and Tabs forms open in a full-page editor (<code>/forms/:id/records/new</code>) instead of a dialog so you get the screen real estate.</p>
            </>
        ),
    },
    {
        id: "sections",
        icon: Stack,
        title: "Sections",
        body: () => (
            <>
                <p>Sections group related fields and give the form structure. From the Builder:</p>
                <ol>
                    <li>Open the form's Builder page.</li>
                    <li>Click <strong>+ Section</strong>, give it a title and an optional description.</li>
                    <li>Drag fields into the section, or set <em>Section</em> in the field editor.</li>
                </ol>
                <p>The right-hand panel groups fields under their section as a collapsible tree. Forms without sections show a synthetic "All Fields" bucket.</p>
            </>
        ),
    },
    {
        id: "fields",
        icon: FilesIcon,
        title: "Fields",
        body: () => (
            <>
                <p>17 field types supported: <code>text</code>, <code>textarea</code>, <code>number</code>, <code>decimal</code>, <code>phone</code>, <code>email</code>, <code>date</code>, <code>datetime</code>, <code>dropdown</code>, <code>multiselect</code>, <code>checkbox</code>, <code>radio</code>, <code>toggle</code>, <code>file</code>, <code>image</code>, <code>lookup</code>, <code>table</code>.</p>
                <p><strong>Flags</strong> (in the field editor): required, unique, read-only, hidden, searchable, sortable, filterable, mobile-only, desktop-only, group default.</p>
                <p><strong>Auto-generate value</strong> (text/number fields) — generates sequential IDs like <code>PO-001</code>, <code>PO-002</code>. Configure prefix, suffix, padding, start.</p>
                <p><strong>Table field</strong> — for line items (invoices, packing lists). Columns can be <code>text</code>, <code>textarea</code>, <code>number</code>, <code>date</code>, <code>dropdown</code>. Number columns can auto-sum (Σ) in a totals row.</p>
                <p><strong>File / image fields</strong> — 5 MB max per file. Images get an inline preview; documents (PDF, Word, Excel, PowerPoint, text, CSV, JSON) get a download chip.</p>
                <p><strong>Lookup field</strong> — picks a record from another form. In the field editor, scroll to <em>Lookup configuration</em> → pick a target form → pick a Display field (what the user sees) → optionally pick a Stored value field (defaults to record id). The renderer shows a searchable combobox; the value stored on save is the target record id while the cell shows the human-readable label.</p>
                <p><strong>Dependent dropdowns</strong> — on any dropdown field, scroll to <em>Dependent dropdown</em> → pick a parent dropdown → enter the options that should appear for each parent value. The child stays disabled until the parent is filled; changing the parent value resets the child.</p>
                <p><strong>Dependent lookups</strong> — on a lookup field, enable <em>Make this a dependent lookup</em>: pick the parent field on THIS form and the column on the TARGET form they should match. The lookup options now filter to only records where the target column equals the parent's value.</p>
            </>
        ),
    },
    {
        id: "workflows",
        icon: GitBranch,
        title: "Workflows",
        body: () => (
            <>
                <p>Approvals route records through one or more steps. From the form Builder, click <strong>Workflow</strong>:</p>
                <ol>
                    <li>Enable the workflow toggle.</li>
                    <li>Add steps. Each step has a name and an assignee (a user, a group, or a role).</li>
                    <li><strong>Per-step controls:</strong> for each field, pick <em>Hide / Read-only / Mandatory / Inherit</em>. For each section, you can flip a single "Hide whole section" toggle which is faster than configuring every field individually.</li>
                </ol>
                <p>When a record is submitted, the first-step approvers receive an email with one-click <strong>Approve</strong> / <strong>Reject</strong> magic links — no login required. The token is HMAC-signed and expires in 14 days.</p>
                <p>Org admins can view the full audit at <Link to="/workflow-history" className="text-primary underline">/workflow-history</Link>.</p>
            </>
        ),
    },
    {
        id: "publish",
        icon: ShareNetwork,
        title: "Publishing & sharing",
        body: () => (
            <>
                <p>Forms are private by default. To share:</p>
                <ul>
                    <li><strong>Internal share</strong> — drop into a group's Form Access list. Members get create/read/update/delete based on the auto-generated per-form roles.</li>
                    <li><strong>Public link</strong> — Builder → Share → enable Public. Optionally set a PIN. Public submissions are gated by a math captcha.</li>
                </ul>
            </>
        ),
    },
    {
        id: "users-groups",
        icon: UsersThree,
        title: "Users, groups, roles",
        body: () => (
            <>
                <p><strong>Users</strong> — Users → New user. One user belongs to one org.</p>
                <p><strong>Groups</strong> — Groups → New group. Add members and assigned roles. Both lists have a search box for fast lookup.</p>
                <p><strong>Roles</strong> — every form auto-generates four system roles: <code>{`<form_code>_create`}</code>, <code>_read</code>, <code>_update</code>, <code>_delete</code>, plus an <code>_all</code> wildcard. You can also create custom roles. Deleting a form cascades — its roles, comments, sequences, and group access references are all removed automatically.</p>
            </>
        ),
    },
    {
        id: "defaults",
        icon: Sparkle,
        title: "Defaults",
        body: () => (
            <>
                <p>Pre-fill fields based on who's submitting the record.</p>
                <ol>
                    <li>In the field editor, enable the <strong>Group default</strong> flag.</li>
                    <li>Either set <em>group-level default values</em> (Groups → Edit → Default values) <strong>or</strong> per-form via Access Configs in the Builder.</li>
                </ol>
                <p><strong>Precedence (highest wins):</strong> user-typed value &gt; per-form access config &gt; group default &gt; field's static default.</p>
                <p>Defaults are re-fetched the moment a user clicks "New record", so admin changes take effect without a refresh.</p>
            </>
        ),
    },
    {
        id: "import-export",
        icon: Database,
        title: "Import / export",
        body: () => (
            <>
                <p><strong>Form schema</strong> — Builder → Export downloads a JSON bundle of the form (sections, fields, auto-generate, table_columns, options). Import the same JSON into another workspace to bootstrap a clone. Note: lookup-form references are not portable; rewire them after import.</p>
                <p><strong>Records</strong> — from the records list:</p>
                <ul>
                    <li>Excel: includes the main columns; table-type fields get their OWN sheet per field with flattened rows.</li>
                    <li>PDF: per-record export renders nested table fields as a heading + a sub-table per row.</li>
                </ul>
            </>
        ),
    },
    {
        id: "themes",
        icon: Palette,
        title: "Themes",
        body: () => (
            <>
                <p>The platform admin defines themes (HSL color tokens + typography). Each user can pick one in their <Link to="/profile" className="text-primary underline">Profile</Link>.</p>
                <p><strong>Org default:</strong> as an org admin, click "Set as org default" on a theme. Every user who hasn't picked one of their own will inherit the org default. Users who explicitly chose a theme keep their preference.</p>
            </>
        ),
    },
    {
        id: "billing",
        icon: ListNumbers,
        title: "Billing & packs",
        body: () => (
            <>
                <p>FormFlo prices in <strong>packs</strong>. The base pack ships with your subscription; add expansion packs as your team grows.</p>
                <p>Each pack adds users / forms / records / GB of storage in fixed multiples. The Billing page shows live usage vs. limits.</p>
            </>
        ),
    },
    {
        id: "zoho-payments",
        icon: ListNumbers,
        title: "Zoho Payments — one-time setup",
        body: () => (
            <>
                <p>FormFlo uses Zoho Payments to accept INR online. Setup is a 5-minute, one-time process for the platform admin. Stay in <strong>DEMO mode</strong> until every step is done — demo mode credits the org instantly without contacting Zoho, perfect for testing.</p>
                <ol className="space-y-2 list-decimal pl-5">
                    <li><strong>Create a Zoho Payments account</strong> at <a href="https://www.zoho.com/in/payments/signup/" target="_blank" rel="noopener noreferrer" className="underline text-primary">zoho.com/in/payments/signup</a> and complete the bank-verification process.</li>
                    <li><strong>Note your Account ID</strong> from the Zoho Payments dashboard URL — e.g. <code>payments.zoho.in/app/60123…/dashboard</code> → Account ID is <code>60123…</code>.</li>
                    <li><strong>Generate API Key + Signing Key</strong> at Zoho Payments → Settings → Developer Space → Authentication Keys.</li>
                    <li><strong>Register a Self Client OAuth app</strong> at <a href="https://api-console.zoho.in/add?client_type=ORG" target="_blank" rel="noopener noreferrer" className="underline text-primary">api-console.zoho.in/add?client_type=ORG</a>. Choose "Self Client" → save → note the <strong>Client ID</strong> and <strong>Client Secret</strong>.</li>
                    <li><strong>Generate an authorization code</strong>: open your Self Client → Generate Code tab → scope = <code>ZohoPay.payments.CREATE,ZohoPay.payments.READ</code> → Time Duration = 10 minutes → Generate. <strong>Copy the code immediately</strong> — it&apos;s valid for ~1 minute.</li>
                    <li><strong>Exchange the code for a refresh token</strong> from a terminal (substitute your values):
                        <pre className="mt-2 bg-secondary p-3 rounded-sm font-mono text-[10px] overflow-x-auto whitespace-pre-wrap break-all">
{`curl -X POST 'https://accounts.zoho.in/oauth/v2/token' \\
  -d 'grant_type=authorization_code' \\
  -d 'client_id=YOUR_CLIENT_ID' \\
  -d 'client_secret=YOUR_CLIENT_SECRET' \\
  -d 'code=YOUR_AUTH_CODE'`}
                        </pre>
                        The response contains <code>refresh_token</code> — copy it. It does <strong>not</strong> expire unless revoked.
                    </li>
                    <li>In FormFlo → Platform → Billing settings, paste: <strong>Account ID</strong>, <strong>API Key</strong>, <strong>Signing Key</strong>, <strong>OAuth Client ID</strong>, <strong>OAuth Client Secret</strong>, <strong>OAuth Refresh Token</strong>. Save.</li>
                    <li>Switch <strong>Demo mode OFF</strong>. Try a signup or a pack purchase. The widget should open Zoho&apos;s checkout — pay ₹1 with a test card to confirm end-to-end.</li>
                </ol>
                <p>FormFlo caches the access token in memory and refreshes it 5 minutes before it expires (1 h lifetime). No further action needed.</p>
            </>
        ),
    },
    {
        id: "troubleshooting",
        icon: Wrench,
        title: "Troubleshooting",
        body: () => (
            <>
                <p><strong>"My default isn't filling in"</strong> — check the field has the <em>Group default</em> flag, and that the value is keyed by <em>field_name</em> (not field_label). Try clicking "New record" again — defaults re-fetch live.</p>
            </>
        ),
    },
];

export default function HelpPage() {
    const [q, setQ] = useState("");

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return SECTIONS;
        return SECTIONS.filter((s) => s.title.toLowerCase().includes(term) || s.id.includes(term));
    }, [q]);

    useEffect(() => {
        // Smooth-scroll deep links from the rail (e.g. /help#workflows)
        if (window.location.hash) {
            const el = document.getElementById(window.location.hash.slice(1));
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="border-b border-border bg-card sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
                    <Link to="/" className="flex items-center gap-2" data-testid="help-home-link">
                        <span className="font-display font-bold text-xl tracking-tight">FormFlo</span>
                        <span className="label-overline">Help</span>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/login" className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">Sign in</Link>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-[240px_1fr] gap-10">
                {/* TOC */}
                <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="help-toc">
                    <div className="relative mb-4">
                        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter topics" className="rounded-sm pl-7 h-9 text-sm" data-testid="help-search" />
                    </div>
                    <nav className="space-y-0.5">
                        {filtered.map((s) => (
                            <a key={s.id} href={`#${s.id}`} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-secondary/60 rounded-sm" data-testid={`help-toc-${s.id}`}>
                                <s.icon size={14} weight="duotone" className="text-primary" />
                                <span>{s.title}</span>
                            </a>
                        ))}
                        {filtered.length === 0 && <div className="text-xs text-muted-foreground px-2">No topics match "{q}"</div>}
                    </nav>
                </aside>

                {/* Content */}
                <main className="max-w-3xl prose prose-sm prose-neutral dark:prose-invert">
                    <h1 className="text-4xl font-display font-bold tracking-tight mb-4" data-testid="help-h1">Help & documentation</h1>
                    <p className="text-base text-muted-foreground mb-10">Everything you need to know to ship your first form and route real work through FormFlo. Use the rail on the left to jump to a topic.</p>
                    {SECTIONS.map((s) => (
                        <section key={s.id} id={s.id} className="mb-12 scroll-mt-24" data-testid={`help-section-${s.id}`}>
                            <h2 className="!text-2xl !font-display !font-semibold tracking-tight flex items-center gap-2 mb-3">
                                <s.icon size={20} weight="duotone" className="text-primary" />
                                {s.title}
                                <a href={`#${s.id}`} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground" aria-label="Anchor link"><ArrowSquareOut size={12} /></a>
                            </h2>
                            <div className="text-sm leading-relaxed">{s.body()}</div>
                        </section>
                    ))}
                </main>
            </div>
        </div>
    );
}
